import { v4 as uuid } from 'uuid';
import message from '@meitu/saas-ui/es/message';
import {
    getImgInfoAsync,
    getWebmInfoAsync,
    IMAGE_TYPE,
    isWebm as isWebmFn,
    loadImageAsync,
    svg2Png,
    uploadVectorFile,
} from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit/obs-manager';
import { parseVector } from '@meitu/saas-editor-core/es/utils/vector-kit';
import { MAX_STAGE_HEIGHT, MAX_STAGE_WIDTH } from '@meitu/saas-editor-core/es/version-types';
import { saveSvgParams } from '@meitu/saas-editor-ui/es/components/image-upload';
import { FileType, getFileType, imageUploader, UploadStatusCode } from '@meitu/xdesign-widgets/dist/uploadManager';
import { EVENT_FILES_UPLOAD, events } from '@/events-bus';
import { getLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { MAX_IMAGE_FILE_SIZE } from '@/types';
import { getMediaType } from '@/utils/layer';

interface IFileResult {
    url: string;
    id: string;
    name: string;
    source?: string;
    width: number;
    height: number;
    blobUrl: any;
    svgInfo: string;
    mediaLayerInfo: any;
}
const sleep = (timeout = 1000) =>
    new Promise((resolve, reject) => {
        setTimeout(resolve, timeout);
    });
// 通用支持上传的图片格式
const WIDGETS_IMAGE_TYPE = ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'image/webp', 'image/gif'];

const useFilesUpload = () => {
    const { $t } = getLocaleStore();
    return {
        filesUpload: async (files: File[], source = '', useLoading = true) => {
            const showLoading = () => {
                if (useLoading) editor.tip.showMask({ loading: true });
            };

            const hideLoading = () => {
                if (useLoading) editor.tip.hideMask();
            };

            // eslint-disable-next-line no-async-promise-executor
            return new Promise<IFileResult[]>(async (resolve, reject) => {
                const resultList: IFileResult[] = [];

                showLoading();

                for (let i = 0; i < files.length; i++) {
                    let file = files[i] as any;

                    if (file.size > MAX_IMAGE_FILE_SIZE) {
                        message.error($t((v) => v.collapse.tips.uploadLimit));
                        hideLoading();
                        reject();
                        return;
                    }
                    try {
                        const id = uuid();
                        const fileType = file ? await getFileType(file) : '';
                        let svgInfo: saveSvgParams | undefined;

                        // 格式不支持
                        if (!file || !IMAGE_TYPE.includes(fileType)) {
                            hideLoading();
                            reject();
                            message.warning($t((v) => v.collapse.messageError.filetypeLimit));
                            return;
                        }

                        // 组件内上传
                        if (WIDGETS_IMAGE_TYPE.includes(fileType)) {
                            try {
                                const { url, width, height } = await imageUploader.checkAndUploadFileToObs(file, {
                                    autoConvertWebp: false,
                                    autoConvertGif: false,
                                    limitConfig: {
                                        autoResizeWH: MAX_STAGE_WIDTH,
                                        maxSize: MAX_IMAGE_FILE_SIZE * 1024 * 1024,
                                        accepts: WIDGETS_IMAGE_TYPE as FileType[],
                                    },
                                });
                                const { isMediaLayer, mediaType } = getMediaType(url);
                                resultList.push({
                                    url,
                                    id,
                                    name: (file?.name as string) || '',
                                    source,
                                    width,
                                    height,
                                    blobUrl: '',
                                    svgInfo: JSON.stringify(svgInfo),
                                    mediaLayerInfo: isMediaLayer
                                        ? {
                                              isMediaLayer,
                                              mediaType,
                                          }
                                        : null,
                                });
                                if (resultList.length === files.length) {
                                    resolve(resultList);
                                }
                            } catch (e: any) {
                                let errMsg = $t((v) => v.collapse.messageError.parsingFailed);
                                if (e.code === UploadStatusCode.FileTooLarge) {
                                    errMsg = $t((v) => v.collapse.tips.uploadLimit);
                                }
                                if (e.code === UploadStatusCode.ImageTooLarge) {
                                    errMsg = $t((v, { r }) =>
                                        r(v.collapse.tips.sizeLimit, [MAX_STAGE_WIDTH, MAX_STAGE_HEIGHT]),
                                    );
                                }
                                if (e.code === UploadStatusCode.ConvertError) {
                                    errMsg = $t((v) => v.collapse.messageError.heicError);
                                }
                                message.error(errMsg);
                                hideLoading();
                                reject();
                            }
                        } else {
                            if (file.type === 'image/svg+xml') {
                                try {
                                    const { pngFile, svgEl } = await svg2Png(file);
                                    const fileObs = await uploadFileToObs(file);
                                    const { text, info } = await parseVector(svgEl);
                                    const imageUrl = await uploadVectorFile(text);
                                    svgInfo = {
                                        image: imageUrl.url,
                                        info,
                                        file: fileObs.url,
                                    };
                                    file = pngFile;
                                } catch (error) {
                                    console.error('转换失败', error);
                                }
                            }
                            const isWebm = isWebmFn(file);

                            const blobUrl = (file && window.URL.createObjectURL(file)) || '';

                            // 获取视频webm宽高
                            const loadInfo = isWebm ? getWebmInfoAsync(blobUrl) : loadImageAsync(blobUrl, false);

                            const info: any = await loadInfo;
                            const width = isWebm ? info.videoWidth : info.width;
                            const height = isWebm ? info.videoHeight : info.height;

                            if (width > MAX_STAGE_WIDTH || height > MAX_STAGE_HEIGHT) {
                                message.error(
                                    $t((v, { r }) => r(v.collapse.tips.sizeLimit, [MAX_STAGE_WIDTH, MAX_STAGE_HEIGHT])),
                                );
                                hideLoading();
                                reject();
                            }

                            const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';

                            uploadFileToObs(file, undefined, undefined, fileExtension)
                                .then(async (data: any) => {
                                    const { url } = data;
                                    const { isMediaLayer, mediaType } = getMediaType(url);
                                    resultList.push({
                                        url,
                                        id,
                                        name: (file?.name as string) || '',
                                        source,
                                        width,
                                        height,
                                        blobUrl,
                                        svgInfo: JSON.stringify(svgInfo),
                                        mediaLayerInfo: isMediaLayer
                                            ? {
                                                  isMediaLayer,
                                                  mediaType,
                                              }
                                            : null,
                                    });
                                    if (resultList.length === files.length) {
                                        resolve(resultList);
                                    }
                                })
                                .catch((error: any) => {
                                    resultList.push({
                                        url: '',
                                        id: '',
                                        name: '',
                                        source: '',
                                        width: 0,
                                        height: 0,
                                        blobUrl: '',
                                        svgInfo: '',
                                        mediaLayerInfo: null,
                                    });
                                    if (resultList.length === files.length) {
                                        resolve(resultList);
                                    }
                                });
                        }
                        hideLoading();
                    } catch (error) {
                        hideLoading();
                        reject();
                        console.log('error', error);
                        message.error($t((v) => v.collapse.messageError.parsingFailed));
                    }
                }
            });
        },
    };
};

export const saveMyPictureByUrl = async (file: IFileResult) => {};

export default useFilesUpload;
